import { useStripe } from '@stripe/react-stripe-js'
import type { Stripe, Card as StripeIssuingCard } from '@stripe/stripe-js'
import { CardDetailsDocument } from 'generated/resources/graphql'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import type { ApplicationError } from 'modules/core/lib/asyncAction'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface IssuingCard extends StripeIssuingCard {
  pin?: {
    number: string
  }
}

export interface CardDetails {
  issuingCard: IssuingCard
}

interface RetrieveIssuingCardParams {
  nonce: string
  ephemeralKeySecret: string
  expand: string[]
}

export interface StripeAPI extends Stripe {
  createEphemeralKeyNonce: ({ issuingCard }: { issuingCard: string }) => { nonce: string }
  retrieveIssuingCard: (cardId: string, params: RetrieveIssuingCardParams) => Promise<CardDetails>
}

type ExpandOptions = 'number' | 'cvc' | 'pin.number'

const useCardDetails = (externalCardId: string, cardId: string, expand: ExpandOptions[]) => {
  const stripe = useStripe() as StripeAPI
  const [createEphemeralKey, { loading }] = useMutation(CardDetailsDocument, {
    extractorFn: (data) => data?.bankingMutations.createBankingCardEphemeralKey,
  })

  const loadCardDetails = async () => {
    const nonce = (await stripe?.createEphemeralKeyNonce({ issuingCard: externalCardId })).nonce
    const result = await createEphemeralKey({ variables: { cardId, nonce } })
    const handled = handleResult(result, () => true)

    if (handled.success && handled.data.cardEphemeralKey?.secret) {
      const ephemeralKeySecret = handled.data.cardEphemeralKey?.secret

      return stripe?.retrieveIssuingCard(externalCardId, {
        nonce,
        ephemeralKeySecret,
        expand: expand,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  const { running, value, performAction } = useAsyncAction(
    async () => {
      return loadCardDetails()
    },
    {
      successFlash: false,
      errorFlash: true,
    }
  )

  return {
    loadCardDetails: performAction,
    loadingCardDetails: running || loading,
    cardDetails: value,
  }
}

export default useCardDetails

export const isCardDetails = (
  value: ApplicationError | CardDetails | undefined | null
): value is CardDetails => {
  if (!value) return false

  return (value as CardDetails).issuingCard !== undefined
}
